import React, { useEffect, useState, useRef } from "react";
import { PlatformService } from "../../services/platformService";

/* ─── Types ─── */
interface SubsystemHealth {
  status:      "healthy" | "degraded" | "unhealthy" | "backlogged" | "unknown";
  latency?:    number;
  detail?:     string;
  pendingJobs?: number;
  activePlans?: number;
}

interface HealthState {
  api:      SubsystemHealth;
  db:       SubsystemHealth;
  whatsapp: SubsystemHealth;
  ocr:      SubsystemHealth;
  queue:    SubsystemHealth;
  ts?:      string;
}

const POLL_MS = 60_000; // 60 s

/* ─── Colour helpers ─── */
const STATUS_DOT: Record<string, string> = {
  healthy:    "bg-emerald-500",
  degraded:   "bg-amber-400",
  backlogged: "bg-amber-400",
  unhealthy:  "bg-red-500",
  unknown:    "bg-slate-300 animate-pulse",
};

const STATUS_LABEL: Record<string, string> = {
  healthy:    "text-emerald-700",
  degraded:   "text-amber-700",
  backlogged: "text-amber-700",
  unhealthy:  "text-red-600",
  unknown:    "text-slate-400",
};

const dotClass   = (s?: string) => STATUS_DOT[s ?? "unknown"]   ?? STATUS_DOT.unknown;
const labelClass = (s?: string) => STATUS_LABEL[s ?? "unknown"] ?? STATUS_LABEL.unknown;

/* ─── Individual node ─── */
const Node = ({
  label, sub, showDetail,
}: {
  label: string;
  sub?: SubsystemHealth;
  showDetail: boolean;
}) => {
  const status = sub?.status ?? "unknown";
  const detail: string[] = [];
  if (sub?.latency   != null) detail.push(`${sub.latency}ms`);
  if (sub?.pendingJobs != null && sub.pendingJobs > 0) detail.push(`${sub.pendingJobs} pending`);
  if (sub?.activePlans != null) detail.push(`${sub.activePlans} active`);
  if (sub?.detail)               detail.push(sub.detail);

  return (
    <div className="flex items-center gap-1.5 group relative">
      <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${dotClass(status)}`} />
      <span className={`text-[10px] font-bold uppercase tracking-wider ${labelClass(status)}`}>
        {label}
      </span>
      {/* Hover tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none
                      opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
        <div className="bg-slate-900 text-white text-[10px] rounded-lg px-2.5 py-1.5 whitespace-nowrap
                        shadow-lg flex flex-col gap-0.5">
          <span className={`font-bold uppercase ${dotClass(status).replace("bg-", "text-").replace("-500", "-400").replace("-400", "-400")}`}>
            {status}
          </span>
          {detail.map((d, i) => <span key={i} className="text-slate-300">{d}</span>)}
          {!detail.length && <span className="text-slate-400">no details</span>}
        </div>
        <div className="w-2 h-2 bg-slate-900 rotate-45 mx-auto -mt-1" />
      </div>
    </div>
  );
};

/* ─── Overall indicator ─── */
const overallStatus = (h: HealthState | null): "healthy" | "degraded" | "unhealthy" => {
  if (!h) return "healthy";
  const statuses = [h.api?.status, h.db?.status, h.whatsapp?.status, h.ocr?.status, h.queue?.status];
  if (statuses.some(s => s === "unhealthy")) return "unhealthy";
  if (statuses.some(s => s === "degraded" || s === "backlogged")) return "degraded";
  return "healthy";
};

/* ════════════════════════════════════════════════════════════════════════ */

const PlatformHealthBar: React.FC = () => {
  const [health,      setHealth]      = useState<HealthState | null>(null);
  const [expanded,    setExpanded]    = useState(false);
  const [loading,     setLoading]     = useState(true);
  const intervalRef   = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetch = async () => {
    try {
      const data = await PlatformService.getHealth();
      setHealth(data);
    } catch {
      // On failure, mark api as unhealthy
      setHealth(prev => prev
        ? { ...prev, api: { status: "unhealthy" } }
        : null
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
    intervalRef.current = setInterval(fetch, POLL_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const overall = overallStatus(health);

  return (
    <div className="relative">
      {/* ── Collapsed: single indicator button ── */}
      <button
        onClick={() => setExpanded(e => !e)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-all ${
          overall === "unhealthy"
            ? "border-red-200 bg-red-50 hover:bg-red-100"
            : overall === "degraded"
            ? "border-amber-200 bg-amber-50 hover:bg-amber-100"
            : "border-emerald-100 bg-emerald-50/60 hover:bg-emerald-50"
        }`}
        title="Click to see subsystem health"
      >
        {loading ? (
          <span className="h-1.5 w-1.5 rounded-full bg-slate-300 animate-pulse" />
        ) : (
          <span className={`h-1.5 w-1.5 rounded-full ${
            overall === "unhealthy" ? "bg-red-500" :
            overall === "degraded"  ? "bg-amber-400" :
            "bg-emerald-500 animate-pulse"
          }`} />
        )}
        <span className={`text-[10px] font-bold uppercase tracking-wider hidden sm:block ${
          overall === "unhealthy" ? "text-red-700" :
          overall === "degraded"  ? "text-amber-700" :
          "text-emerald-700"
        }`}>
          {loading ? "Checking…" : overall === "unhealthy" ? "Degraded" : overall === "degraded" ? "Warning" : "All Systems OK"}
        </span>
      </button>

      {/* ── Expanded: subsystem panel ── */}
      {expanded && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setExpanded(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 bg-surface border border-border rounded-2xl shadow-xl p-4 min-w-[260px]">
            <p className="text-[9px] font-bold text-muted/50 uppercase tracking-[0.2em] mb-3">
              Subsystem Status
            </p>
            <div className="space-y-2.5">
              {([
                { key: "api",      label: "API Gateway" },
                { key: "db",       label: "Database" },
                { key: "whatsapp", label: "WhatsApp" },
                { key: "ocr",      label: "OCR Engine" },
                { key: "queue",    label: "Job Queue" },
              ] as const).map(({ key, label }) => {
                const sub = health?.[key];
                return (
                  <div key={key} className="flex items-center justify-between gap-4">
                    <Node label={label} sub={sub} showDetail />
                    <div className="flex items-center gap-1.5 text-[10px] text-muted">
                      {sub?.latency   != null && <span>{sub.latency}ms</span>}
                      {sub?.pendingJobs != null && sub.pendingJobs > 0 && (
                        <span className="text-amber-600 font-semibold">{sub.pendingJobs} pending</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {health?.ts && (
              <p className="text-[9px] text-muted/50 mt-3 pt-2.5 border-t border-border">
                Last check {new Date(health.ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PlatformHealthBar;
